/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { impostazioniAPI } from '../api/impostazioni';

// Nome, descrizione, logo e icona dell'installazione. Stanno nel database e li
// imposta l'admin: nel codice non deve comparire il nome di nessuna azienda.
// I valori di riserva servono solo finche' la risposta non e' arrivata.
const RISERVA = {
  app_nome: 'Gestionale',
  app_descrizione: '',
  app_logo_url: null,
  app_favicon_url: null,
};

const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL || '';
const API_URL = process.env.REACT_APP_API_URL || '';

// Il database contiene un percorso tipo "/uploads/branding/logo-ab12.png";
// REACT_APP_UPLOADS_URL punta gia' a .../uploads, quindi il prefisso va tolto.
export function urlImmagine(percorso) {
  if (!percorso) return null;
  if (/^https?:\/\//i.test(percorso)) return percorso;
  return UPLOADS_URL + percorso.replace(/^\/uploads/, '');
}

export function useImpostazioni() {
  const { data } = useQuery({
    queryKey: ['impostazioni'],
    queryFn: () => impostazioniAPI.leggi(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  return data || RISERVA;
}

// Aggiunge in testa alla pagina un elemento che l'HTML del build non ha, oppure
// riusa quello che c'e' gia'.
function elementoInTesta(selettore, crea) {
  let el = document.querySelector(selettore);
  if (!el) {
    el = crea();
    document.head.appendChild(el);
  }
  return el;
}

// Vero se sull'home screen si puo' usare un'immagine dell'installazione. Un ICO non va:
// iOS non lo accetta come icona dell'home screen, e l'API non sa rimpicciolirlo.
function haIconaPropria(imp, area) {
  const url = imp[`app_icona_${area}_url`] || imp.app_logo_url || imp.app_favicon_url;
  return Boolean(url) && !/\.ico$/i.test(url);
}

// Titolo della scheda, icona del browser e — cosa diversa — nome e icona che il
// telefono usa quando l'app viene aggiunta alla schermata Home.
//
// Sono due meccanismi separati: il favicon della scheda non c'entra niente con
// l'home screen. iOS legge l'apple-touch-icon e apple-mobile-web-app-title dalla
// pagina aperta, quindi si aggiornano qui; Android legge il manifest, che serve
// l'API e che nginx inoltra su /manifest.json (un manifest di un'altra origine
// verrebbe scartato, perche' start_url e scope sono relativi al manifest stesso).
// `area` e' la chiave tecnica ('cliente', 'tecnico', 'admin'), `etichetta` quello che
// si legge nel titolo della scheda ("Area Cliente").
export function useIdentitaApp(area, etichetta) {
  const imp = useImpostazioni();

  // Titolo e icona possono essere impostati per la singola dashboard: e' l'unico modo
  // di distinguere le tre app quando finiscono sull'home screen dello stesso telefono.
  // Vuoti, vale l'identita' generale.
  const nomeProprio = (imp[`app_nome_${area}`] || '').trim();
  const nome = nomeProprio || imp.app_nome || RISERVA.app_nome;
  const icona = urlImmagine(imp[`app_icona_${area}_url`] || imp.app_favicon_url || imp.app_logo_url);
  const iconaHomeScreen = haIconaPropria(imp, area)
    ? `${API_URL}/impostazioni/icona-180.png?area=${encodeURIComponent(area)}`
    : null;

  // Un nome proprio e' gia' specifico: aggiungerci l'etichetta darebbe
  // "Cantieri · Area Tecnico".
  const titolo = nomeProprio || (etichetta ? `${nome} · ${etichetta}` : nome);

  useEffect(() => {
    document.title = titolo;
  }, [titolo]);

  // Nome proposto da iOS nella finestra "Aggiungi a Home": senza questo prenderebbe
  // il titolo intero, area compresa.
  useEffect(() => {
    const meta = elementoInTesta('meta[name="apple-mobile-web-app-title"]', () => {
      const m = document.createElement('meta');
      m.name = 'apple-mobile-web-app-title';
      return m;
    });
    meta.content = nome;
  }, [nome]);

  useEffect(() => {
    if (!icona) return;
    const link = elementoInTesta("link[rel~='icon']", () => {
      const l = document.createElement('link');
      l.rel = 'icon';
      return l;
    });
    link.href = icona;
  }, [icona]);

  // Se il logo non c'e' si lascia in piedi quello del build: un apple-touch-icon
  // che risponde 404 farebbe finire sull'home screen una miniatura della pagina.
  useEffect(() => {
    if (!iconaHomeScreen) return;
    const link = elementoInTesta("link[rel='apple-touch-icon']", () => {
      const l = document.createElement('link');
      l.rel = 'apple-touch-icon';
      return l;
    });
    link.href = iconaHomeScreen;
  }, [iconaHomeScreen]);

  return imp;
}
